/**
 * Shared, non-UI taxonomy for agent-connection model providers.
 *
 * Lives in `src/utils` so both stores (`@/stores/connectionStore`) and UI
 * helpers (`@/components/WorkflowDesigner/workflowHelpers`) can depend on it
 * without the store pulling in a component-layer module — which would invert
 * the dependency direction and risk circular imports.
 */

/**
 * Normalized identifiers for the model provider behind an agent connection.
 * Mirrors the values accepted by an Agent action's `agentModelType` parameter.
 */
export type AgentModelProvider =
  | 'AzureOpenAI'
  | 'OpenAI'
  | 'GitHubCopilot'
  | 'FoundryAgentService'
  | 'FoundryAgentServiceV2'
  | 'MicrosoftManaged';

/**
 * Data-plane connector id of the GitHub sign-in that backs a GitHub Copilot agent
 * connection. Both the OAuth credential and its cascade-delete key off this id.
 */
export const GITHUB_SERVICE_PROVIDER_CONNECTOR_ID = '/serviceProviders/github';

/** Fixed provider endpoint persisted on a GitHub Copilot agent connection. */
export const GITHUB_COPILOT_ENDPOINT = 'https://api.githubcopilot.com';

const KNOWN_AGENT_PROVIDERS: readonly AgentModelProvider[] = [
  'FoundryAgentServiceV2',
  'FoundryAgentService',
  'MicrosoftManaged',
  'AzureOpenAI',
  'OpenAI',
  'GitHubCopilot',
];

/**
 * Resolve any free-form identifier (explicit provider name, endpoint URL, api id path segment, …) to its canonical `AgentModelProvider`.
 * This should not be run on resourceIds since they may contain substrings that match provider patterns incorrectly.
 * Returns `undefined` when nothing matches.
 *
 * Exact (case-insensitive) matches win; substring fallbacks handle path-shaped
 * inputs. FoundryV2 must be checked before V1 since `'foundryagentservice'`
 * is a prefix of both.
 */
export const normalizeProviderName = (value: string | undefined): AgentModelProvider | undefined => {
  if (!value) return undefined;
  const lower = value.toLowerCase();
  const exact = KNOWN_AGENT_PROVIDERS.find((p) => p.toLowerCase() === lower);
  if (exact) return exact;
  if (lower.includes('foundryagentservicev2')) return 'FoundryAgentServiceV2';
  if (lower.includes('/projects/')) return 'FoundryAgentService';
  // MicrosoftManaged must be checked before AzureOpenAI since 'azureopenai'
  // is a suffix of 'microsoftmanagedazureopenai'-style accidental composite inputs. The runtime distinguishes them by the
  // synthetic connection reference name, not by endpoint host, so exact-match
  // above is the primary path; this guard covers accidental substring inputs.
  if (lower.includes('microsoftmanaged')) return 'MicrosoftManaged';
  if (lower.includes('.cognitiveservices.') || lower.includes('azureopenai')) return 'AzureOpenAI';
  if (lower.includes('.openai.azure.com')) return 'AzureOpenAI';
  if (lower.includes('.openai.')) return 'OpenAI';
  if (lower.includes('githubcopilot') || lower.includes('ghcp')) return 'GitHubCopilot';
  return undefined;
};

/** Agent providers backed by Azure AI Foundry. */
export const FOUNDRY_AGENT_PROVIDERS: ReadonlySet<AgentModelProvider> = new Set<AgentModelProvider>([
  'FoundryAgentService',
  'FoundryAgentServiceV2',
]);

/**
 * Agent providers backed by native model services for a *workflow* (native)
 * agent. GitHub Copilot is intentionally excluded here — GHCP is a coding-agent
 * feature and shouldn't be surfaced in the native agent connection picker.
 * MicrosoftManaged is a valid native workflow-agent provider — it's
 * the same shape as AzureOpenAI but with a Microsoft-managed endpoint pool.
 */
export const NATIVE_AGENT_PROVIDERS: ReadonlySet<AgentModelProvider> = new Set<AgentModelProvider>([
  'AzureOpenAI',
  'OpenAI',
  'MicrosoftManaged',
]);

/**
 * Agent providers offered for a *coding* agent. Managed agents run on the GHCP
 * harness, so GitHub Copilot connections are valid here in addition to the
 * standard native model services. MicrosoftManaged is NOT yet valid
 * here — the managed-model runtime path doesn't support the coding harness
 * yet, so Managed Agent stays BYOM-only until that lands.
 */
export const CODING_AGENT_PROVIDERS: ReadonlySet<AgentModelProvider> = new Set<AgentModelProvider>([
  'AzureOpenAI',
  'OpenAI',
  'GitHubCopilot',
]);

/**
 * The exact set of values the backend `AgentConnectionType` enum accepts —
 * see `Flow.Data.Common/Entities/Agent/AgentConnectionType.cs`. Anything
 * written as a literal `agentConnections[*].type` in connections.json must collapse
 * to one of these or backend validation will reject the workflow.
 */
export const BACKEND_AGENT_CONNECTION_TYPES = [
  'Model',
  'FoundryAgentService',
  'APIMGenAIGateway',
  'GitHubCopilot',
] as const;

export type BackendAgentConnectionType = (typeof BACKEND_AGENT_CONNECTION_TYPES)[number];

/**
 * Translate our finer-grained client-side provider names to the backend
 * `AgentConnectionType` enum before persisting to `agentConnections[*].type`.
 */
export const toBackendAgentConnectionType = (
  clientType: string | undefined,
): BackendAgentConnectionType => {
  if (!clientType) return 'Model';
  const normalized = normalizeProviderName(clientType);
  if (normalized && FOUNDRY_AGENT_PROVIDERS.has(normalized)) return 'FoundryAgentService';
  if (clientType === 'APIMGenAIGateway') return 'APIMGenAIGateway';
  // GitHub Copilot is its own backend connection type: the runtime keys auth
  // brokerage and endpoint selection off `connection.Type == GitHubCopilot`,
  // so it must be persisted verbatim rather than collapsed to 'Model'.
  if (normalized === 'GitHubCopilot') return 'GitHubCopilot';
  // Microsoft-managed is intentionally absent: those refs are NOT
  // persisted to connections.json (see `connectionStore.exportToConnectionsConfig`
  // and `managedModelReference.ts`). The runtime dispatches on the
  // action's `agentModelType` — there is no connection-layer Microsoft-managed type on
  // either side of the wire, so nothing on the write path asks this mapper
  // for Microsoft-managed.
  return 'Model';
};
